import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { AccountingIntegration } from 'src/app/data/enums/accounting-integration';
import { TransactionAgentCoding, TransactionLedgerRow } from 'src/app/data/model/transaction-ledger';
import { CurrencyCode } from 'src/app/modules/bookkeeping/enums/currency-code';
import { TaxBasis } from 'src/app/modules/bookkeeping/enums/taxBasis';
import { AiCodingSuggestion, ManualCodingPayload } from 'src/app/shared/components/manual-coding-modal/manual-coding-modal.component';
import { OrganizationService } from './organization.service';

const VISIBLE_KEY = 'synced.showCodingSuggestions';

/**
 * SD-1254. The Coding Assistant's suggested coding on the transactions table: whether the user
 * wants to see it, and accepting it in one click.
 *
 * Accepting sends exactly what the coding modal sends when it is opened on a suggestion and
 * confirmed unchanged: the suggested supplier and account, with the organisation's default tax
 * rate, currency and tax basis, as the modal fills them.
 */
@Injectable({
  providedIn: 'root'
})
export class SuggestedCodingService {
  private readonly visible = new BehaviorSubject<boolean>(readVisible());
  readonly visible$ = this.visible.asObservable();

  constructor(private organizationService: OrganizationService) { }

  get isVisible(): boolean { return this.visible.value; }

  /** Per browser, like the table's column choices: a preference, not a setting. */
  setVisible(visible: boolean): void {
    try { localStorage.setItem(VISIBLE_KEY, visible ? '1' : '0'); } catch { /* storage blocked */ }
    this.visible.next(visible);
  }

  /**
   * How sure the suggested supplier is. An older backend sends no verdict: a supplier it named
   * counts as matched, as it always did.
   */
  static supplierMatch(c: TransactionAgentCoding | null): 'matched' | 'possible' | 'new' | 'none' {
    if (!c) { return 'none'; }
    if (c.supplierMatch) { return c.supplierMatch; }
    return c.contactId ? 'matched' : 'none';
  }

  /** Accept as is only when the supplier is one of the organisation's and sure. */
  static canAccept(row: TransactionLedgerRow): boolean {
    const c = row && row.agentCoding;
    return !!c && !!c.accountId && !!c.contactId && SuggestedCodingService.supplierMatch(c) === 'matched';
  }

  /** The coding form's pre-fill for a row's suggestion: the dialog's, and a transaction panel's. */
  static formSuggestion(c: TransactionAgentCoding | null): AiCodingSuggestion | null {
    if (!c || !c.accountId) { return null; }
    const match = SuggestedCodingService.supplierMatch(c);
    const known = match === 'matched' || match === 'possible';
    return {
      supplierId: known ? (c.contactId || '') : '',
      supplierName: known ? (c.contactName || '') : '',
      accountId: c.accountId,
      accountName: c.accountName || c.accountCode || '',
      confidence: c.confidence ?? undefined,
      reason: c.reason ?? undefined,
      supplierMatch: match,
      newSupplierName: match === 'new' ? (c.newSupplierName || undefined) : undefined,
    };
  }

  /** One payload per supplier and account, so rows with the same suggestion post together. */
  acceptPayloads(rows: TransactionLedgerRow[]): Observable<ManualCodingPayload[]> {
    // Coding needs a supplier of the organisation's: the rest are reviewed in the coding form.
    const suggested = (rows || []).filter(r => SuggestedCodingService.canAccept(r));
    if (!suggested.length) { return of([]); }

    const org = this.organizationService.selectedOrganization;
    return forkJoin({
      tax: this.organizationService.getOrganizationTaxRateId(org).pipe(take(1), catchError(() => of(null))),
      currency: this.organizationService.getOrganizationDefaultCurrency(org).pipe(take(1), catchError(() => of(null))),
      integration: this.organizationService.organizationList.pipe(take(1),
        map((list: any[]) => (list || []).find(o => o.organisationID === org)?.accountingIntegration)),
    }).pipe(map(({ tax, currency, integration }) => {
      // As ManualCodingModalComponent.loadOrgTaxRate.
      const noTax = (integration === AccountingIntegration.QBO || integration === AccountingIntegration.None)
        && tax?.taxBasis === TaxBasis.IgnoreTax;
      const taxBasis = noTax ? TaxBasis.IgnoreTax : TaxBasis.DefaultRate;
      const taxRateId = noTax ? '0' : (tax?.taxRateId ?? null);

      const groups = new Map<string, TransactionLedgerRow[]>();
      suggested.forEach(r => {
        const key = `${r.agentCoding.contactId || r.agentCoding.contactName || ''}|${r.agentCoding.accountId}`.toLowerCase();
        groups.set(key, [...(groups.get(key) || []), r]);
      });

      return [...groups.values()].map(group => {
        const c = group[0].agentCoding;
        return {
          transactionIds: group.map(r => r.unreconciledReportId),
          shared: {
            supplierId: c.contactId || '',
            supplierName: c.contactName || '',
            accountId: c.accountId,
            accountName: c.accountName || c.accountCode || '',
            details: '',
            currency: (currency?.currency || null) as CurrencyCode,
            taxRateId,
            taxBasis,
            classId: null,
            costCentreId: null,
            jobs: [],
          },
        } as ManualCodingPayload;
      });
    }));
  }
}

function readVisible(): boolean {
  try { return localStorage.getItem(VISIBLE_KEY) !== '0'; } catch { return true; }
}
